"use client";

import { Button, Loader } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, CheckCircle2, Wrench } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { marketplaceReasonLabel } from "@/modules/merchant/domain/contracts/marketplace-policy";
import {
  canUseMerchantPermission,
  type ShopSellableCatalogDetail,
} from "@/modules/merchant/presentation/api/client";
import { useShopSellableSkuQuery } from "@/modules/merchant/presentation/api/queries";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopCatalogSkuEditForm } from "@/modules/merchant/presentation/ui/ShopCatalogSkuEditForm";
import { ShopSkuMediaSection } from "@/modules/merchant/presentation/ui/ShopSkuMediaSection";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";

const CATALOG_ROUTE = "/app/shops/$shopId/catalog" as const;

/** ສະຖານະ marketplace ຂອງ SKU — fitment / eligibility (ຂໍ້ມູນຈາກ catalog detail) */
function MarketplaceEligibilityBanner({
  detail,
}: {
  detail: ShopSellableCatalogDetail;
}) {
  if (detail.marketplaceEligible) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
        <CheckCircle2
          className="mt-0.5 size-5 shrink-0 text-emerald-600"
          aria-hidden
        />
        <div>
          <p className="font-medium text-emerald-700 text-sm dark:text-emerald-300">
            ພ້ອມຂາຍ Marketplace
          </p>
          <p className="text-muted-foreground text-xs">
            fitment ຄົບ ແລະ ມີສະຕ໋ອກ — ເປີດ auto-publish ເພື່ອຂຶ້ນຂາຍ online
          </p>
        </div>
      </div>
    );
  }

  const reasons = detail.eligibilityReasons
    .map((code) => marketplaceReasonLabel(code))
    .join(" · ");

  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
      <Wrench className="mt-0.5 size-5 shrink-0 text-amber-600" aria-hidden />
      <div>
        <p className="font-medium text-amber-700 text-sm dark:text-amber-300">
          ຍັງຂຶ້ນ Marketplace ບໍ່ໄດ້
        </p>
        <p className="text-muted-foreground text-xs">
          {reasons || "ກວດສອບ fitment ແລະ ສະຕ໋ອກ"} — ຂາຍ POS ໄດ້ປົກກະຕິ
        </p>
      </div>
    </div>
  );
}

export function ShopCatalogSkuEditPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const shopProductSkuId = params?.shopProductSkuId as string | undefined;
  const nav = useNavigate();

  const skuQuery = useShopSellableSkuQuery(shopId, shopProductSkuId);
  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const canUpdateCatalog = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.catalog.update,
    verifiedLevel,
  );

  const goBackToCatalog = () => {
    if (shopId) {
      nav({ to: CATALOG_ROUTE, params: { shopId } });
    }
  };

  return (
    <RequireMerchantPermission permission={MerchantPermissions.catalog.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.catalogSkuEdit(shopId)} />
      </Header>

      <Main>
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-heading font-semibold text-xl tracking-tight">
              ແກ້ໄຂລາຄາ / SKU / Barcode
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ປັບລາຄາ, ສະຕ໋ອກ, ແລະຄ່າເລີ່ມຕົ້ນປະກັນ
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ms-2 mb-3 gap-1 text-muted-foreground"
            onClick={goBackToCatalog}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບໄປລາຍການສິນຄ້າ
          </Button>
        </div>

        {!shopId || !shopProductSkuId ? (
          <p className="text-destructive text-sm">ບໍ່ພົບລາຍການ</p>
        ) : skuQuery.isLoading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : skuQuery.isError || !skuQuery.data ? (
          <div className="rounded-xl border border-border bg-card p-6">
            <p className="text-muted-foreground text-sm">
              ບໍ່ພົບ SKU ຫຼືໂຫຼດລົ້ມເຫຼວ — ກວດສອບວ່າສິນຄ້າຍັງເປີດຂາຍໃນຮ້ານນີ້
            </p>
            <Button
              type="button"
              variant="outline"
              className="mt-4"
              onClick={goBackToCatalog}
            >
              ກັບໄປລາຍການ
            </Button>
          </div>
        ) : !canUpdateCatalog ? (
          <div className="rounded-xl border border-border bg-card p-6">
            <p className="text-muted-foreground text-sm">
              ບັນຊີນີ້ບໍ່ມີສິດແກ້ໄຂລາຍການສິນຄ້າ (catalog:update)
            </p>
            <Button
              type="button"
              variant="outline"
              className="mt-4"
              onClick={goBackToCatalog}
            >
              ກັບໄປລາຍການ
            </Button>
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-4">
            <MarketplaceEligibilityBanner detail={skuQuery.data} />
            <ShopCatalogSkuEditForm
              shopId={shopId}
              row={skuQuery.data}
              onCancel={goBackToCatalog}
              onSaved={goBackToCatalog}
            />
            <ShopSkuMediaSection
              shopId={shopId}
              shopProductSkuId={shopProductSkuId}
              catalogSkuId={skuQuery.data.catalogSkuId}
              canManage={canUpdateCatalog}
            />
          </div>
        )}
      </Main>
    </RequireMerchantPermission>
  );
}
